/**
 * MateriaalBeheer — materialen (types) met exemplaren + sets.
 * Laag 0: grid van materiaal-cards. Laag 1: TypePopup. Laag 2: ExemplaarPopup.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Chip, Tooltip, Tabs, Tab, CircularProgress,
  Dialog, DialogContent, MenuItem, Select, FormControl, Autocomplete,
} from '@mui/material';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/apiConfig';
import { toast } from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import TypePopup from './TypePopup';
import ExemplaarPopup from './ExemplaarPopup';
import SetPopup from './SetPopup';
import IconColorPicker, { MaterialVisual } from './IconColorPicker';
import ReparatieTab from './ReparatieTab';
import { filterGroepen, statusGroep, STATUS_FILTERS, LOCATIES } from './catalogusFilters';

const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5', error: '#a83836', errorBg: '#fef2f2',
  amber: '#f59e0b', amberBg: '#fef3c7',
  font: '"Manrope", system-ui, sans-serif', fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", lineHeight: 1, display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', ...sx }}>{name}</span>
);

const CATEGORY_ICONS = {
  'Cameras': 'videocam', 'Drones': 'flight', 'Lens': 'camera', 'Monitors': 'monitor',
  'Statief': 'straighten', 'Geluid': 'mic', 'Grip': 'handyman', 'Licht': 'lightbulb',
  'Camera Accessoires': 'settings_input_component', 'Special effects': 'auto_awesome',
  'Studio equipment': 'storefront', 'Vervoer en karren': 'local_shipping', 'Kabels': 'cable',
};
const CATEGORY_ORDER = ['Cameras', 'Lens', 'Drones', 'Monitors', 'Statief', 'Geluid', 'Grip', 'Licht', 'Camera Accessoires', 'Kabels', 'Special effects', 'Studio equipment', 'Vervoer en karren'];

const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: '0.55rem', fontFamily: T.fontBody, fontSize: '0.85rem', bgcolor: T.surface } };
const filterSx = { borderRadius: '0.55rem', fontFamily: T.fontBody, fontSize: '0.8rem', bgcolor: T.surface };

// ── Materiaal-card ───────────────────────────────────────────────────────────
export function TypeCard({ group, onClick, index, dimmed = false }) {
  const tf = group.typeFields;
  const name = tf.MateriaalType || 'Onbekend';
  const categorie = group.categorie;
  const count = group.children.length;
  const archived = !!tf.Gearchiveerd && !group.isOrphan;

  return (
    <Box onClick={onClick} sx={{
      bgcolor: T.surface, borderRadius: '0.85rem', border: '1px solid rgba(45,51,53,0.07)',
      overflow: 'hidden', cursor: 'pointer', boxShadow: T.shadow,
      filter: (archived || dimmed) ? 'grayscale(0.9) opacity(0.72)' : 'none',
      transition: 'transform 0.16s, box-shadow 0.16s, border-color 0.16s, filter 0.16s',
      display: 'flex', flexDirection: 'column',
      animation: 'matCardFade 0.4s ease both', animationDelay: `${Math.min(index * 0.025, 0.4)}s`,
      '@keyframes matCardFade': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'none' } },
      '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 10px 28px rgba(45,51,53,0.12)', borderColor: `${T.primary}30`, filter: archived ? 'grayscale(0.4) opacity(0.92)' : 'none' },
    }}>
      {/* Visual */}
      <Box sx={{ position: 'relative', aspectRatio: '16 / 10', bgcolor: T.surfaceLow, overflow: 'hidden' }}>
        <MaterialVisual imageUrl={tf.Afbeelding} icon={tf.Icoon} color={tf.Kleur} fallbackIcon={CATEGORY_ICONS[categorie] || 'inventory_2'} radius={0} iconSize={46} />
        {/* Icoon-badge ook tonen als er een foto is */}
        {tf.Afbeelding && tf.Icoon && (
          <Box sx={{ position: 'absolute', bottom: 8, left: 8, width: 26, height: 26, borderRadius: '0.4rem', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.92)', boxShadow: '0 1px 4px rgba(0,0,0,0.12)' }}>
            <Icon name={tf.Icoon} size={16} sx={{ color: tf.Kleur || T.primary }} />
          </Box>
        )}
        {count > 1 && (
          <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.7, py: 0.25, borderRadius: '0.4rem', bgcolor: 'rgba(255,255,255,0.92)', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
            <Icon name="inventory_2" size={12} sx={{ color: T.primary }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.66rem', fontWeight: 800, color: T.primary }}>{count}×</Typography>
          </Box>
        )}
        {archived && (
          <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.7, py: 0.25, borderRadius: '0.4rem', bgcolor: 'rgba(45,51,53,0.82)' }}>
            <Icon name="archive" size={12} sx={{ color: '#fff' }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 800, color: '#fff', letterSpacing: '0.02em' }}>Gearchiveerd</Typography>
          </Box>
        )}
      </Box>
      {/* Body */}
      <Box sx={{ p: 1.5, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography sx={{ fontFamily: T.font, fontSize: '0.9rem', fontWeight: 700, color: T.text, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {name}
        </Typography>
        {tf.GeenReserveringNodig && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, alignSelf: 'flex-start', px: 0.6, py: 0.2, borderRadius: '0.35rem', bgcolor: T.amberBg, border: `1px solid ${T.amber}40` }}>
            <Icon name="all_inclusive" size={12} sx={{ color: T.amber }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 800, color: T.amber }}>Geen reservering nodig</Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', gap: 1 }}>
          {categorie ? (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, minWidth: 0 }}>
              <Icon name={CATEGORY_ICONS[categorie] || 'category'} size={13} sx={{ color: T.outline }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 500, color: T.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{categorie}</Typography>
            </Box>
          ) : <span />}
          {count === 0 && (
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 600, color: T.outlineVariant, fontStyle: 'italic', flexShrink: 0 }}>geen exemplaren</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default function MateriaalBeheer() {
  const [materials, setMaterials] = useState([]);
  const [materiaalSets, setMateriaalSets] = useState([]);
  const [materiaalTypes, setMateriaalTypes] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  const { tab } = useParams();
  const navigate = useNavigate();
  const TABS = ['materialen', 'sets', 'reparatie'];
  const selectedTab = Math.max(0, TABS.indexOf(tab));
  const handleTabChange = (_, v) => navigate(`/materiaal-beheer/${TABS[v] || 'materialen'}`);

  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [sortBy, setSortBy] = useState('naam');
  // Catalogusfilters naast Categorie (26 sep): status, materiaaltype, opslaglocatie
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState(null);
  const [locatieFilter, setLocatieFilter] = useState('');

  const [openTypeKey, setOpenTypeKey] = useState(null);
  const [openExemplaarId, setOpenExemplaarId] = useState(null);
  const [openSetId, setOpenSetId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showArchivedSets, setShowArchivedSets] = useState(false);

  // Nieuw materiaal
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', category: '', icon: null, color: null, noReservation: false, metExemplaar: true });
  const [creating, setCreating] = useState(false);

  const fetchMaterials = useCallback(async () => {
    try { const res = await axios.get(`${BACKEND_URL}/Api/materialen?includeArchived=true`); setMaterials(res.data || []); }
    catch (err) { console.error('Error fetching materials:', err); toast.error('Kan materialen niet laden'); }
  }, []);
  const fetchSets = useCallback(async () => {
    try { const res = await axios.get(`${BACKEND_URL}/Api/materiaalsets`); setMateriaalSets(res.data?.records || res.data || []); }
    catch (err) { console.error('Error fetching materiaalsets:', err); }
  }, []);
  const fetchTypes = useCallback(async () => {
    try { const res = await axios.get(`${BACKEND_URL}/Api/materiaaltypes`); setMateriaalTypes(res.data?.records || res.data || []); }
    catch (err) { console.error('Error fetching materiaaltypes:', err); }
  }, []);
  const fetchReservations = useCallback(async () => {
    try { const res = await axios.get(`${BACKEND_URL}/Api/reservations`); setReservations(res.data || []); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchMaterials(), fetchSets(), fetchTypes(), fetchReservations()]);
      setLoading(false);
    };
    load();
  }, [fetchMaterials, fetchSets, fetchTypes, fetchReservations]);

  const refetchAll = useCallback(async () => {
    await Promise.all([fetchMaterials(), fetchTypes(), fetchSets()]);
  }, [fetchMaterials, fetchTypes, fetchSets]);

  // Type -> [exemplaren]
  const groupedByType = useMemo(() => {
    const matById = {};
    materials.forEach(m => { matById[m.id] = m; });
    const groups = materiaalTypes.map(mt => {
      const tf = mt.fields || {};
      const children = (tf.Materialen || []).map(id => matById[id]).filter(Boolean);
      return { type: mt, typeFields: tf, children, categorie: tf.Categorie || 'Overig' };
    });
    const linkedMatIds = new Set(materiaalTypes.flatMap(mt => mt.fields?.Materialen || []));
    materials.filter(m => !linkedMatIds.has(m.id)).forEach(m => {
      groups.push({
        type: { id: `orphan_${m.id}`, fields: { MateriaalType: m.fields?.ID || 'Onbekend', Categorie: m.fields?.Categorie } },
        typeFields: { MateriaalType: m.fields?.ID || 'Onbekend', Categorie: m.fields?.Categorie },
        children: [m], categorie: m.fields?.Categorie || 'Overig', isOrphan: true,
      });
    });
    return groups;
  }, [materials, materiaalTypes]);

  // Totaal niet-gearchiveerde exemplaren (voor tab-label)
  const totalExemplaren = useMemo(() =>
    groupedByType.filter(g => !(g.typeFields.Gearchiveerd && !g.isOrphan)).reduce((s, g) => s + g.children.length, 0),
    [groupedByType]);

  // Tellingen per categorie volgen de overige filters, anders kloppen de chips niet met het grid.
  const categoryCounts = useMemo(() => {
    const basis = filterGroepen(groupedByType, { status: statusFilter, typeId: typeFilter?.id, locatie: locatieFilter });
    const counts = { Alle: basis.length };
    basis.forEach(g => { counts[g.categorie] = (counts[g.categorie] || 0) + 1; });
    return counts;
  }, [groupedByType, statusFilter, typeFilter, locatieFilter]);

  const filteredGroups = useMemo(() => {
    let list = filterGroepen(groupedByType, { status: statusFilter, typeId: typeFilter?.id, locatie: locatieFilter });
    if (selectedCategory !== 'Alle') list = list.filter(g => g.categorie === selectedCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(g => {
        const nameMatch = (g.typeFields.MateriaalType || '').toLowerCase().includes(q);
        const childMatch = g.children.some(c => (c.fields?.ID || '').toLowerCase().includes(q) || (c.fields?.SerieNummer || '').toLowerCase().includes(q));
        return nameMatch || childMatch;
      });
    }
    const catIdx = (c) => (CATEGORY_ORDER.indexOf(c) >= 0 ? CATEGORY_ORDER.indexOf(c) : 99);
    const isArch = (g) => (g.typeFields.Gearchiveerd && !g.isOrphan) ? 1 : 0;
    return [...list].sort((a, b) => {
      if (isArch(a) !== isArch(b)) return isArch(a) - isArch(b); // gearchiveerd onderaan
      const na = a.typeFields.MateriaalType || '', nb = b.typeFields.MateriaalType || '';
      if (sortBy === 'naam') return na.localeCompare(nb);
      if (sortBy === 'categorie') return catIdx(a.categorie) - catIdx(b.categorie) || na.localeCompare(nb);
      if (sortBy === 'meestGebruikt') {
        const rc = (g) => g.children.reduce((s, c) => s + (c.fields?.aantalReserveringen || 0), 0);
        return rc(b) - rc(a);
      }
      return 0;
    });
  }, [groupedByType, selectedCategory, search, sortBy, statusFilter, typeFilter, locatieFilter]);

  const typeOpties = useMemo(() => groupedByType
    .filter(g => !g.isOrphan)
    .map(g => ({ id: g.type.id, label: g.typeFields.MateriaalType || 'Onbekend' }))
    .sort((a, b) => a.label.localeCompare(b.label)), [groupedByType]);
  const aantalOpReparatie = useMemo(() => materials.filter(m => statusGroep(m.fields?.Status, m.fields?.Reparatiestatus) === 'reparatie').length, [materials]);
  const filtersActief = !!(statusFilter || typeFilter || locatieFilter);

  const getMaterialReservations = useCallback((materialId) => {
    return reservations.filter(r => (r.fields?.Materiaal || []).includes(materialId)).map(r => ({
      id: r.id, start: r.fields?.Start, eind: r.fields?.Eind,
      draaidag: r.fields?.DraaidagNaam?.[0] || r.fields?.DraaidagNaam || 'Onbekend',
      nummer: r.fields?.reserveringNummer || r.fields?.ReserveringNummer,
      door: r.fields?.SubmitterName || r.fields?.ModifiedByName || null,
    })).sort((a, b) => (a.start || '').localeCompare(b.start || ''));
  }, [reservations]);

  const openGroup = useMemo(() => (openTypeKey ? groupedByType.find(g => g.type.id === openTypeKey) || null : null), [openTypeKey, groupedByType]);
  const openExemplaar = useMemo(() => (openExemplaarId ? materials.find(m => m.id === openExemplaarId) || null : null), [openExemplaarId, materials]);
  const openSet = useMemo(() => (openSetId ? materiaalSets.find(s => s.id === openSetId) || null : null), [openSetId, materiaalSets]);

  const handleCreateMaterial = async () => {
    const name = newForm.name.trim();
    if (!name) { toast.error('Naam is verplicht'); return; }
    setCreating(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/Api/materiaaltypes`, {
        name, category: newForm.category || null, icon: newForm.icon, color: newForm.color,
        no_reservation_required: newForm.noReservation,
      });
      const newTypeId = r.data?.data?.record?.id;
      if (newTypeId && newForm.metExemplaar) await axios.post(`${BACKEND_URL}/Api/materialen`, { materiaalTypeId: newTypeId });
      toast.success(newForm.metExemplaar ? 'Materiaal aangemaakt' : 'Materiaaltype aangemaakt (nog zonder exemplaren)');
      setNewOpen(false);
      setNewForm({ name: '', category: '', icon: null, color: null, noReservation: false, metExemplaar: true });
      await refetchAll();
      if (newTypeId) setOpenTypeKey(newTypeId);
    } catch (err) {
      toast.error('Aanmaken mislukt: ' + (err.response?.data?.error || err.message));
    } finally { setCreating(false); }
  };

  // Sets: zoeken + sorteren + gearchiveerd onderaan
  const [setsQuery, setSetsQuery] = useState('');
  const filteredSets = useMemo(() => {
    let list = materiaalSets;
    if (setsQuery.trim()) {
      const q = setsQuery.toLowerCase();
      list = list.filter(s => (s.fields?.ProjectType || '').toLowerCase().includes(q) || (s.fields?.Omschrijving || '').toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      const aa = a.fields?.Gearchiveerd ? 1 : 0, bb = b.fields?.Gearchiveerd ? 1 : 0;
      if (aa !== bb) return aa - bb;
      return (a.fields?.ProjectType || '').localeCompare(b.fields?.ProjectType || '');
    });
  }, [materiaalSets, setsQuery]);

  return (
    <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10, minHeight: '100vh' }}>
      <PageHeader
        icon="inventory_2" title="Materiaalbeheer" subtitle="Beheer alle materialen, exemplaren en sets"
        rightActions={(
          <>
            <Chip label={`${totalExemplaren} exemplaren`} size="small" sx={{ fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.72rem', bgcolor: T.primaryContainer, color: T.primary }} />
            <Chip label={`${materiaalSets.length} sets`} size="small" sx={{ fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.72rem', bgcolor: T.tertiaryContainer, color: T.tertiary }} />
          </>
        )}
      />

      <Tabs value={selectedTab} onChange={handleTabChange}
        sx={{ mb: 2, '& .MuiTab-root': { fontFamily: T.font, fontWeight: 700, fontSize: '0.85rem', textTransform: 'none', minHeight: 40 }, '& .Mui-selected': { color: T.primary }, '& .MuiTabs-indicator': { bgcolor: T.primary, height: 3, borderRadius: 2 } }}>
        <Tab label={`Materialen (${totalExemplaren})`} />
        <Tab label={`Sets (${materiaalSets.length})`} />
        <Tab label={`Reparatie (${aantalOpReparatie})`} />
      </Tabs>

      {selectedTab === 0 && (
        <>
          {/* Search + sort + nieuw */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Zoek materiaal of exemplaar…" value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Icon name="search" size={18} sx={{ color: T.outline }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 200, ...inputSx }} />
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              {[{ key: 'naam', label: 'Naam', icon: 'sort_by_alpha' }, { key: 'categorie', label: 'Categorie', icon: 'category' }, { key: 'meestGebruikt', label: 'Meest gebruikt', icon: 'trending_up' }].map(opt => (
                <Tooltip key={opt.key} title={`Sorteer op ${opt.label.toLowerCase()}`} arrow>
                  <Box onClick={() => setSortBy(opt.key)} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 1.25, py: 0.6, borderRadius: '0.5rem', cursor: 'pointer', bgcolor: sortBy === opt.key ? T.primary : T.surfaceLow, color: sortBy === opt.key ? '#fff' : T.secondary, fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.72rem', transition: 'all 0.15s', '&:hover': { bgcolor: sortBy === opt.key ? T.primaryDim : T.surfaceHigh } }}>
                    <Icon name={opt.icon} size={15} sx={{ color: sortBy === opt.key ? '#fff' : T.outline }} />
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{opt.label}</Box>
                  </Box>
                </Tooltip>
              ))}
            </Box>
            <Box onClick={() => setNewOpen(true)} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.7, borderRadius: '0.5rem', cursor: 'pointer', background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`, color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.75rem', flexShrink: 0, boxShadow: '0 4px 12px rgba(99,89,131,0.22)', '&:hover': { boxShadow: '0 6px 18px rgba(99,89,131,0.32)' } }}>
              <Icon name="add" size={17} sx={{ color: '#fff' }} /> Nieuw materiaal
            </Box>
          </Box>

          {/* Filters: status, materiaaltype, opslaglocatie */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <Select value={statusFilter} displayEmpty onChange={(e) => setStatusFilter(e.target.value)} sx={filterSx}>
                <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Alle statussen</MenuItem>
                {STATUS_FILTERS.map(o => <MenuItem key={o.key} value={o.key} sx={{ fontSize: '0.8rem' }}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Autocomplete size="small" options={typeOpties} value={typeFilter} onChange={(_, v) => setTypeFilter(v)}
              getOptionLabel={(o) => o.label} isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => <TextField {...params} placeholder="Alle materiaaltypes" sx={inputSx} />}
              sx={{ width: 240 }} />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select value={locatieFilter} displayEmpty onChange={(e) => setLocatieFilter(e.target.value)} sx={filterSx}>
                <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Alle locaties</MenuItem>
                {LOCATIES.map(l => <MenuItem key={l} value={l} sx={{ fontSize: '0.8rem' }}>{l}</MenuItem>)}
                <MenuItem value="__geen__" sx={{ fontSize: '0.8rem' }}><em>Zonder locatie</em></MenuItem>
              </Select>
            </FormControl>
            {filtersActief && (
              <Typography onClick={() => { setStatusFilter(''); setTypeFilter(null); setLocatieFilter(''); }}
                sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', fontWeight: 600, color: T.primary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                Filters wissen
              </Typography>
            )}
          </Box>

          {/* Category chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5, minHeight: 36 }}>
            <Chip label={`Alle (${categoryCounts.Alle || 0})`} onClick={() => setSelectedCategory('Alle')}
              sx={{ fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.72rem', bgcolor: selectedCategory === 'Alle' ? T.primary : T.surfaceLow, color: selectedCategory === 'Alle' ? '#fff' : T.secondary, '&:hover': { bgcolor: selectedCategory === 'Alle' ? T.primaryDim : T.surfaceHigh } }} />
            {CATEGORY_ORDER.filter(c => categoryCounts[c]).map(cat => (
              <Chip key={cat} icon={<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18 }}><Icon name={CATEGORY_ICONS[cat] || 'category'} size={15} sx={{ color: selectedCategory === cat ? '#fff' : T.outline }} /></Box>}
                label={`${cat} (${categoryCounts[cat] || 0})`} onClick={() => setSelectedCategory(cat)}
                sx={{ fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.72rem', bgcolor: selectedCategory === cat ? T.primary : T.surfaceLow, color: selectedCategory === cat ? '#fff' : T.secondary, '&:hover': { bgcolor: selectedCategory === cat ? T.primaryDim : T.surfaceHigh }, '& .MuiChip-icon': { ml: 0.75, mr: -0.25 } }} />
            ))}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={32} sx={{ color: T.tertiary }} /></Box>
          ) : (() => {
            const activeGroups = filteredGroups.filter(g => !(g.typeFields.Gearchiveerd && !g.isOrphan));
            const archivedGroups = filteredGroups.filter(g => (g.typeFields.Gearchiveerd && !g.isOrphan));
            return (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2 }}>
                  {activeGroups.map((group, i) => (
                    <TypeCard key={group.type.id} group={group} index={i} onClick={() => setOpenTypeKey(group.type.id)} />
                  ))}
                </Box>
                {activeGroups.length === 0 && archivedGroups.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Icon name="search_off" size={48} sx={{ color: T.outlineVariant }} />
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', color: T.outline, mt: 1 }}>Geen materialen gevonden</Typography>
                  </Box>
                )}

                {/* Gearchiveerd — ingeklapte dropdown onderaan */}
                {archivedGroups.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Box onClick={() => setShowArchived(v => !v)} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, cursor: 'pointer', borderTop: `1px solid ${T.surfaceHigh}`, userSelect: 'none' }}>
                      <Icon name={showArchived ? 'expand_more' : 'chevron_right'} size={20} sx={{ color: T.outline }} />
                      <Icon name="archive" size={16} sx={{ color: T.outline }} />
                      <Typography sx={{ fontFamily: T.font, fontSize: '0.85rem', fontWeight: 700, color: T.secondary }}>
                        Gearchiveerd ({archivedGroups.length})
                      </Typography>
                    </Box>
                    {showArchived && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2, mt: 1 }}>
                        {archivedGroups.map((group, i) => (
                          <TypeCard key={group.type.id} group={group} index={i} onClick={() => setOpenTypeKey(group.type.id)} />
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </>
            );
          })()}
        </>
      )}

      {/* Sets tab */}
      {selectedTab === 1 && (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Zoek set…" value={setsQuery} onChange={(e) => setSetsQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Icon name="search" size={18} sx={{ color: T.outline }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 200, ...inputSx }} />
            <Box onClick={() => setOpenSetId('__new__')} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.7, borderRadius: '0.5rem', cursor: 'pointer', background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`, color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.75rem', flexShrink: 0, boxShadow: '0 4px 12px rgba(99,89,131,0.22)' }}>
              <Icon name="add" size={17} sx={{ color: '#fff' }} /> Nieuwe set
            </Box>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={32} sx={{ color: T.tertiary }} /></Box>
          ) : (() => {
            const renderSetCard = (set) => {
              const f = set.fields || {};
              const archived = !!f.Gearchiveerd;
              const aantalMat = (f.MaterialenFromMaterialenType || []).length;
              return (
                <Box key={set.id} onClick={() => setOpenSetId(set.id)} sx={{ bgcolor: T.surface, borderRadius: '0.85rem', border: '1px solid rgba(45,51,53,0.07)', p: 2, cursor: 'pointer', boxShadow: T.shadow, opacity: archived ? 0.58 : 1, transition: 'all 0.15s', '&:hover': { borderColor: `${T.tertiary}40`, transform: 'translateY(-2px)', boxShadow: '0 8px 22px rgba(45,51,53,0.1)' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: '0.55rem', flexShrink: 0, overflow: 'hidden' }}>
                      <MaterialVisual icon={f.Icoon || 'backpack'} color={f.Kleur || T.tertiary} fallbackIcon="backpack" iconSize={20} />
                    </Box>
                    <Typography sx={{ flex: 1, minWidth: 0, fontFamily: T.font, fontSize: '0.95rem', fontWeight: 700, color: T.text, lineHeight: 1.25, wordBreak: 'break-word' }}>{f.ProjectType || 'Onbekende set'}</Typography>
                    {archived
                      ? <Chip label="Gearchiveerd" size="small" sx={{ fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.6rem', bgcolor: T.surfaceHigh, color: T.outline }} />
                      : <Chip label={`${aantalMat} materialen`} size="small" sx={{ fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.65rem', bgcolor: T.tertiaryContainer, color: T.tertiary }} />}
                  </Box>
                  {f.Omschrijving && <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outline, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{f.Omschrijving}</Typography>}
                </Box>
              );
            };
            const activeSets = filteredSets.filter(s => !s.fields?.Gearchiveerd);
            const archivedSets = filteredSets.filter(s => s.fields?.Gearchiveerd);
            return (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 2 }}>
                  {activeSets.map(renderSetCard)}
                </Box>
                {activeSets.length === 0 && archivedSets.length === 0 && (
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.outline, py: 4 }}>Geen sets gevonden</Typography>
                )}
                {archivedSets.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Box onClick={() => setShowArchivedSets(v => !v)} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, cursor: 'pointer', borderTop: `1px solid ${T.surfaceHigh}`, userSelect: 'none' }}>
                      <Icon name={showArchivedSets ? 'expand_more' : 'chevron_right'} size={20} sx={{ color: T.outline }} />
                      <Icon name="archive" size={16} sx={{ color: T.outline }} />
                      <Typography sx={{ fontFamily: T.font, fontSize: '0.85rem', fontWeight: 700, color: T.secondary }}>Gearchiveerd ({archivedSets.length})</Typography>
                    </Box>
                    {showArchivedSets && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 2, mt: 1 }}>
                        {archivedSets.map(renderSetCard)}
                      </Box>
                    )}
                  </Box>
                )}
              </>
            );
          })()}
        </>
      )}

      {selectedTab === 2 && (
        <ReparatieTab materials={materials} onOpenExemplaar={(id) => setOpenExemplaarId(id)} onChanged={fetchMaterials} />
      )}

      {/* Laag 1 — TypePopup */}
      <TypePopup
        open={!!openGroup} group={openGroup} allSets={materiaalSets}
        onClose={() => setOpenTypeKey(null)} onChanged={refetchAll}
        onExemplaarClick={(mat) => setOpenExemplaarId(mat.id)}
        getMaterialReservations={getMaterialReservations}
      />

      {/* Laag 2 — ExemplaarPopup */}
      <ExemplaarPopup
        open={!!openExemplaar} material={openExemplaar}
        onClose={() => setOpenExemplaarId(null)} onChanged={refetchAll}
        getMaterialReservations={getMaterialReservations}
      />

      {/* SetPopup (bewerken / nieuw) */}
      <SetPopup
        open={!!openSet || openSetId === '__new__'}
        set={openSetId === '__new__' ? null : openSet}
        allTypes={materiaalTypes}
        onClose={() => setOpenSetId(null)} onChanged={refetchAll}
      />

      {/* Nieuw materiaal-dialog */}
      <Dialog open={newOpen} onClose={() => !creating && setNewOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '1rem' } }}>
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon name="add_box" size={22} sx={{ color: T.primary }} />
          <Typography sx={{ fontFamily: T.font, fontSize: '1.05rem', fontWeight: 800, color: T.text }}>Nieuw materiaal</Typography>
        </Box>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField autoFocus size="small" fullWidth label="Naam" value={newForm.name} onChange={(e) => setNewForm(p => ({ ...p, name: e.target.value }))} sx={inputSx} />
          <FormControl size="small" fullWidth>
            <Select value={newForm.category} onChange={(e) => setNewForm(p => ({ ...p, category: e.target.value }))} displayEmpty sx={{ borderRadius: '0.55rem', fontFamily: T.fontBody, fontSize: '0.85rem' }}>
              <MenuItem value=""><em>Categorie kiezen…</em></MenuItem>
              {CATEGORY_ORDER.map(c => <MenuItem key={c} value={c} sx={{ fontFamily: T.fontBody, fontSize: '0.85rem' }}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <IconColorPicker icon={newForm.icon} color={newForm.color} onChange={({ icon, color }) => setNewForm(p => ({ ...p, icon, color }))} label="Icoon & kleur (zonder foto)" />
          {/* Reservering nodig / Geen reservering nodig */}
          <Box>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 700, color: T.outlineVariant, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>Reserveren</Typography>
            <Box sx={{ display: 'inline-flex', borderRadius: '0.5rem', overflow: 'hidden', border: `1px solid ${T.surfaceHigh}` }}>
              {[{ v: false, l: 'Reservering nodig' }, { v: true, l: 'Geen reservering nodig' }].map(o => (
                <Box key={String(o.v)} onClick={() => setNewForm(p => ({ ...p, noReservation: o.v }))} sx={{ px: 1.5, py: 0.6, cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.72rem', bgcolor: newForm.noReservation === o.v ? T.primary : T.surface, color: newForm.noReservation === o.v ? '#fff' : T.secondary }}>{o.l}</Box>
              ))}
            </Box>
            {newForm.noReservation && (
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.66rem', color: T.outline, mt: 0.5 }}>Exemplaren geven nooit conflicten (genoeg in magazijn).</Typography>
            )}
          </Box>
          <Box onClick={() => setNewForm(p => ({ ...p, metExemplaar: !p.metExemplaar }))} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', userSelect: 'none' }}>
            <Icon name={newForm.metExemplaar ? 'check_box' : 'check_box_outline_blank'} size={20} sx={{ color: newForm.metExemplaar ? T.primary : T.outlineVariant }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.text }}>Direct één exemplaar aanmaken</Typography>
          </Box>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outline }}>
            {newForm.metExemplaar
              ? 'Foto, opslaglocatie en meer kun je daarna in het materiaal bewerken.'
              : 'Alleen het materiaaltype; exemplaren voeg je later toe in het materiaal.'}
          </Typography>
        </DialogContent>
        <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Box onClick={() => !creating && setNewOpen(false)} sx={{ px: 2, py: 0.7, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.8rem', color: T.secondary, '&:hover': { bgcolor: T.surfaceLow } }}>Annuleren</Box>
          <Box onClick={() => !creating && handleCreateMaterial()} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 2.5, py: 0.7, borderRadius: '0.5rem', cursor: creating ? 'default' : 'pointer', background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`, color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.8rem', opacity: creating ? 0.7 : 1 }}>
            {creating && <CircularProgress size={14} sx={{ color: '#fff' }} />} Aanmaken
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
